import { jsPDF } from "jspdf";

/**
 * QR code export helpers — PNG, PDF, ZPL.
 *
 * The on-screen QR is fetched from api.qrserver.com so for high-quality
 * export we ask the same service for a print-resolution PNG (1024 px).
 */

const QR_API = "https://api.qrserver.com/v1/create-qr-code/";

const slugify = (s) =>
  (s || "qr")
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64) || "qr";

const triggerDownload = (blobOrUrl, filename) => {
  const a = document.createElement("a");
  a.href = blobOrUrl instanceof Blob ? URL.createObjectURL(blobOrUrl) : blobOrUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  if (blobOrUrl instanceof Blob) {
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }
};

/** Fetch a high-resolution QR PNG as a Blob. */
async function fetchQrPng(url, size = 1024) {
  const src = `${QR_API}?data=${encodeURIComponent(url)}&size=${size}x${size}&margin=20&format=png`;
  const res = await fetch(src);
  if (!res.ok) throw new Error("QR fetch failed");
  return res.blob();
}

/** Read a Blob as data: URL for jsPDF. */
function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

/** Export PNG: ~1024 px square, ready for sticker labels or office printers. */
export async function exportQrPng({ url, label }) {
  const blob = await fetchQrPng(url, 1024);
  triggerDownload(blob, `${slugify(label)}-qr.png`);
}

/**
 * Export PDF: A4 portrait with a centred QR (90 mm), human-readable label and
 * the underlying URL printed below. Optimised to be cut out as a 100×100 mm
 * sticker if needed.
 */
export async function exportQrPdf({ url, label, subLabel }) {
  const blob = await fetchQrPng(url, 1024);
  const dataUrl = await blobToDataUrl(blob);
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth(); // 210
  const qrSize = 90;
  const qrX = (pageW - qrSize) / 2;
  const qrY = 35;

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(label || "QR", pageW / 2, 22, { align: "center" });

  // QR
  doc.addImage(dataUrl, "PNG", qrX, qrY, qrSize, qrSize);

  // Sub-label (mode hint, eg "Quick book")
  if (subLabel) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text(subLabel, pageW / 2, qrY + qrSize + 12, { align: "center" });
  }

  // URL footer (monospace-ish, small)
  doc.setFontSize(8);
  doc.setTextColor(120);
  const wrapped = doc.splitTextToSize(url, pageW - 30);
  doc.text(wrapped, pageW / 2, qrY + qrSize + 22, { align: "center" });

  doc.setTextColor(160);
  doc.setFontSize(7);
  doc.text("Generated by ReSource Build", pageW / 2, qrY + qrSize + 40, { align: "center" });

  doc.save(`${slugify(label)}-qr.pdf`);
}

/**
 * Export ZPL: Zebra Programming Language for thermal label printers.
 * Defaults assume a 4×4 inch (≈ 800×800 dots @ 203 dpi) label format with a
 * QR around 50×50 mm and the label text underneath.
 */
export function buildZpl({ url, label, subLabel }) {
  // Sanitise label text for ZPL (strip backticks, carets and tildes which are
  // ZPL control characters).
  const sanitise = (s) => (s || "").replace(/[\^~`]/g, " ").slice(0, 80);
  const heading = sanitise(label);
  const sub = sanitise(subLabel || "");

  // Magnification 6 gives ~50×50 mm at 203 dpi. Adjust if you use a smaller
  // label format on your printer.
  return [
    "^XA",
    "^CI28",                       // UTF-8 encoding
    "^PW812",                       // print width — fits 4 inch (203 dpi)
    "^LH0,0",
    // QR code (model 2, magnification 6, error correction Q)
    `^FO80,30^BQN,2,6^FDQA,${url}^FS`,
    // Heading text
    `^FO80,470^A0N,38,38^FB620,2,0,L,0^FD${heading}^FS`,
    sub ? `^FO80,540^A0N,26,26^FB620,2,0,L,0^FD${sub}^FS` : "",
    // URL footer
    `^FO80,720^A0N,18,18^FB620,2,0,L,0^FD${url}^FS`,
    "^PQ1",
    "^XZ",
  ].filter(Boolean).join("\n");
}

export function exportQrZpl({ url, label, subLabel }) {
  const zpl = buildZpl({ url, label, subLabel });
  const blob = new Blob([zpl], { type: "text/plain;charset=utf-8" });
  triggerDownload(blob, `${slugify(label)}-qr.zpl`);
}
